import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import qs from 'qs';
import { RootState } from '../store';
import { apiClientWithApi } from '../../utils/http';
import { OutgoingStatisticsParameters } from '../../hooks/components/useOutgoingStatistics/constants';
import { getStatisticsScenarioMetadata } from '../../models/Task/scenarioMapping';
import { initialState } from './outgoingStatisticsState';
import type {
  OutboundTaskTypeOption,
  OutboundTaskTypesPayload,
  OutgoingStatisticsRequest,
  OutgoingStatisticsResponse,
} from './outgoingStatisticsState';
export type {
  OutboundTaskTypeOption,
  OutboundTaskTypesPayload,
  OutgoingStatisticsState,
  OutgoingStatisticsRequest,
  OutgoingStatisticsResponse,
} from './outgoingStatisticsState';

const buildOutgoingStatisticsParams = (request: OutgoingStatisticsRequest) => {
  const params: Record<string, string | string[]> = {
    [OutgoingStatisticsParameters.From]: request.From,
    [OutgoingStatisticsParameters.To]: request.To,
    [OutgoingStatisticsParameters.ClinicIds]: request.ClinicIds,
  };

  if (request.States?.length) {
    params.States = request.States;
  }

  if (request.AttemptStates?.length) {
    params.AttemptStates = request.AttemptStates;
  }

  if (request.Result?.length) {
    params.Result = request.Result;
  }

  if (request.Types?.length) {
    params.Types = request.Types;
  }

  if (request.FormBatchIds?.length) {
    params.FormBatchIds = request.FormBatchIds;
  }

  if (request.DispensarizationBatchIds?.length) {
    params.DispensarizationBatchIds = request.DispensarizationBatchIds;
  }

  if (request.FormTemplateIds?.length) {
    params.FormTemplateIds = request.FormTemplateIds;
  }

  if (request.TextSearch) {
    params.TextSearch = request.TextSearch;
  }

  return params;
};

export const getOutgoingStatistics = createAsyncThunk(
  'OutgoingStatistics/getOutgoingStatistics',
  async (request: OutgoingStatisticsRequest, { rejectWithValue, signal }) => {
    try {
      const response = await apiClientWithApi.get<OutgoingStatisticsResponse>(
        'task/statistic/outbound',
        {
          params: buildOutgoingStatisticsParams(request),
          paramsSerializer: function (params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
          },
          signal,
        },
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      } else return rejectWithValue('Не удалось получить статистику!');
    }
  },
);

export const getOutboundTaskTypes = createAsyncThunk<OutboundTaskTypesPayload>(
  'OutgoingStatistics/getOutboundTaskTypes',
  async () => {
    const fallback: OutboundTaskTypesPayload = { options: [] };

    try {
      const response =
        await apiClientWithApi.get<unknown>('task/type/outbound');
      const data = response.data;
      if (!Array.isArray(data) || data.length === 0) return fallback;

      const options: OutboundTaskTypeOption[] = [];

      for (const item of data) {
        let key: string | undefined;
        let label: string | undefined;

        if (typeof item === 'string') {
          key = item;
        } else if (item && typeof item === 'object') {
          const obj = item as Record<string, unknown>;
          const rawKey = obj.key ?? obj.value ?? obj.name;
          const rawLabel = obj.value !== obj.key ? obj.value : undefined;
          const rawLabelFallback = obj.text ?? obj.label ?? obj.title;

          if (typeof rawKey === 'string') key = rawKey;
          if (typeof rawLabel === 'string' && rawLabel !== rawKey) {
            label = rawLabel;
          } else if (typeof rawLabelFallback === 'string') {
            label = rawLabelFallback;
          }
        }

        if (!key) continue;

        const metadata = getStatisticsScenarioMetadata(key);
        options.push({
          value: key,
          label: label ?? metadata.optionLabel,
        });
      }

      return options.length > 0 ? { options } : fallback;
    } catch {
      return fallback;
    }
  },
);

export const outgoingStatisticsSlice = createSlice({
  name: 'outgoingStatistics',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(getOutgoingStatistics.pending, (state, action) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
        state.currentRequestId = action.meta.requestId;
        return state;
      })
      .addCase(getOutgoingStatistics.fulfilled, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) {
          return state;
        }

        state.status.responseLoading = false;
        state.status.responseError = null;
        state.currentRequestId = undefined;
        Object.assign(state, action.payload);
        state.lastSuccessfulRequest = action.meta.arg;
        return state;
      })
      .addCase(getOutgoingStatistics.rejected, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) {
          return state;
        }

        state.status.responseLoading = false;
        state.currentRequestId = undefined;
        state.status.responseError = action.meta.aborted
          ? null
          : 'Не удалось загрузить звонки';
        return state;
      })
      .addCase(getOutboundTaskTypes.fulfilled, (state, action) => {
        state.outboundTaskTypeOptions = action.payload.options;
        return state;
      });
  },
});

export default outgoingStatisticsSlice.reducer;

const selectOutgoingStatistics = (state: RootState) => state.outgoingStatistics;

export const tasksCount = (state: RootState) =>
  state.outgoingStatistics.processedTasksCount +
  state.outgoingStatistics.inProcessingTasksCount +
  state.outgoingStatistics.notProcessedTasksCount +
  state.outgoingStatistics.blockedTasksCount;

export const attemptCount = (state: RootState) =>
  state.outgoingStatistics.processedTasksAttemptCount;

export const confirmedTasksCount = (state: RootState) =>
  state.outgoingStatistics.confirmedTasksCount;

export const cancelledTasksCount = (state: RootState) =>
  state.outgoingStatistics.cancelledTasksCount;

export const cancelledByClinicTasksCount = (state: RootState) =>
  state.outgoingStatistics.cancelledByClinicTasksCount;

export const rescheduledTasksCount = (state: RootState) =>
  state.outgoingStatistics.rescheduledTasksCount;

export const rescheduledByClinicTasksCount = (state: RootState) =>
  state.outgoingStatistics.rescheduledByClinicTasksCount;

export const operatorHandedTasksCount = (state: RootState) =>
  state.outgoingStatistics.operatorHandedTasksCount;

export const notIdentifiedTasksCount = (state: RootState) =>
  state.outgoingStatistics.notIdentifiedTasksCount;

export const notReceivedTasksCount = (state: RootState) =>
  state.outgoingStatistics.notReceivedTasksCount;

export const blockedTasksCount = (state: RootState) =>
  state.outgoingStatistics.blockedTasksCount;

export const processedTasksCount = (state: RootState) =>
  state.outgoingStatistics.processedTasksCount;

export const inProcessingTasksCount = (state: RootState) =>
  state.outgoingStatistics.inProcessingTasksCount;

export const notProcessedTasksCount = (state: RootState) =>
  state.outgoingStatistics.notProcessedTasksCount;

export const oneAttemptTasksCount = (state: RootState) =>
  state.outgoingStatistics.oneAttemptBusinessResultTasksCount +
  state.outgoingStatistics.oneAttemptTechnicalResultTasksCount;

export const twoAttemptTasksCount = (state: RootState) =>
  state.outgoingStatistics.twoAttemptBusinessResultTasksCount +
  state.outgoingStatistics.twoAttemptTechnicalResultTasksCount;

export const threeAndMoreAttemptTasksCount = (state: RootState) =>
  state.outgoingStatistics.threeAndMoreAttemptBusinessResultTasksCount +
  state.outgoingStatistics.threeAndMoreAttemptTechnicalResultTasksCount;

export const processedTasksAttemptCount = (state: RootState) =>
  state.outgoingStatistics.processedTasksAttemptCount;

export const failedTasksAttemptCount = (state: RootState) =>
  state.outgoingStatistics.failedTasksAttemptCount;

export const processedTasksBotDetectedAttemptCount = (state: RootState) =>
  state.outgoingStatistics.botDetectedAttemptCount;

export const processedTasksNoAnswerAttemptCount = (state: RootState) =>
  state.outgoingStatistics.noAnswerAttemptCount;

export const processedTasksErrorAttemptCount = (state: RootState) =>
  state.outgoingStatistics.errorAttemptCount;

export const processedTasksHangUpAttemptCount = (state: RootState) =>
  state.outgoingStatistics.hangUpAttemptCount;

export const oneAttemptTasksAttemptCount = (state: RootState) =>
  oneAttemptTasksCount(state);

export const twoAttemptTasksAttemptCount = (state: RootState) =>
  twoAttemptTasksCount(state) * 2;

export const threeAndMoreAttemptTasksAttemptCount = (state: RootState) =>
  state.outgoingStatistics.threeAndMoreAttemptBusinessResultTasksAttemptCount +
  state.outgoingStatistics.threeAndMoreAttemptTechnicalResultTasksAttemptCount;

export const negativeTasksAttemptCount = (state: RootState) =>
  state.outgoingStatistics.oneAttemptTechnicalResultTasksCount +
  state.outgoingStatistics.twoAttemptTechnicalResultTasksCount * 2 +
  state.outgoingStatistics.threeAndMoreAttemptTechnicalResultTasksAttemptCount;

export const selectOutboundTaskTypeOptions = (state: RootState) =>
  state.outgoingStatistics.outboundTaskTypeOptions;

export const callsAttemptCount = attemptCount;
export const tasksUnknownCount = notProcessedTasksCount;
export const callsNotAnswerAttemptCount = processedTasksNoAnswerAttemptCount;
export const tasksConfirmedCount = confirmedTasksCount;
export const tasksDeclinedCount = cancelledTasksCount;
export const tasksOperatorCount = operatorHandedTasksCount;
export const tasksRescheduledCount = rescheduledTasksCount;
export const firstCallsAttempt = oneAttemptTasksCount;
export const secondCallsAttempt = twoAttemptTasksCount;
export const thirdCallsAttempt = threeAndMoreAttemptTasksCount;

export const oneAttemptBusinessResultTasksCount = (state: RootState) =>
  selectOutgoingStatistics(state).oneAttemptBusinessResultTasksCount;

export const oneAttemptTechnicalResultTasksCount = (state: RootState) =>
  selectOutgoingStatistics(state).oneAttemptTechnicalResultTasksCount;

export const twoAttemptBusinessResultTasksCount = (state: RootState) =>
  selectOutgoingStatistics(state).twoAttemptBusinessResultTasksCount;

export const twoAttemptTechnicalResultTasksCount = (state: RootState) =>
  selectOutgoingStatistics(state).twoAttemptTechnicalResultTasksCount;
